/* eslint-disable @typescript-eslint/no-explicit-any */
import { FormikHelpers } from 'formik';
import { AccountInfo, IPublicClientApplication } from '@azure/msal-browser';
import {
    AccountsClient,
    AccountDto,
    GetAccountValuesDto,
    FormStepStatusDto,
    ValidationProblemDetails,
    UsersClient,
} from '../../../api/web-api-client';
import { tokenRequest } from '../../../authentication/authConfig';
import { ErrorType, WizardFormStepValues, WizardStepProps } from '../../../components/forms/WizardForm/types';
import { DiscardProps } from '../../../components/forms/FormikForm/types';
import { AccountContextState } from '../../../authentication/accountContext';
import { NotificationSeverity } from '../../../storage/types';
import { setBranchModalNotification } from '../../../storage/notification';
import { HttpStatusCode } from '../../../types';
import branchSubmitValidation from './validation';

const loadAccountDetails = (accounts: AccountInfo[], instance: IPublicClientApplication) => async (
    abortSignal?: AbortSignal,
) => {
    if (accounts.length > 0) {
        const client = new AccountsClient();
        const tokenResult = await instance.acquireTokenSilent({
            ...tokenRequest,
            account: accounts[0],
        });
        client.setAuthToken(tokenResult.accessToken);

        const businessDetailsStep = await client.getBranchDetails(abortSignal);

        const wizardStepValues: WizardFormStepValues<GetAccountValuesDto> = {
            stepValues: { ...businessDetailsStep.stepValues },
        };
        return wizardStepValues;
    }

    throw Error('There was an error retrieving your organisation and contact details.');
};

const completeAccountDetails = (accounts: AccountInfo[], instance: IPublicClientApplication, accountContext: AccountContextState | null) => async (
    values: AccountDto,
    isDirty: boolean,
    _: FormikHelpers<AccountDto>,
    abortSignal?: AbortSignal,
) => {
    if (accounts.length > 0) {
        try {
            const client = new AccountsClient();
            const tokenResult = await instance.acquireTokenSilent({
                ...tokenRequest,
                account: accounts[0],
            });
            client.setAuthToken(tokenResult.accessToken);
            await client.completeBranchAdd(
                {
                    // formId: 0,
                    formStep: {
                        ...values,
                    },
                    // interim fix for address lookup update,
                    // as using touched/setFieldTouched/setFieldValue doesn't seem to trigger
                    // formik isDirty for the autosuggest unless you blur another control after selection before submission
                    // isDirty: true,
                },
                abortSignal,
            );
            const userClient = new UsersClient();
            await userClient.setAuthToken(tokenResult.accessToken);
            const user = await userClient.signIn({});
            // await accountContext?.setDefaultOrganisationId(user.defaultOrganisationId, user.organisation?.crmGuid);

            if (values.name !== undefined && values.isDefaultOrganisation === true) {
                await accountContext?.setOrganisationAndBranch(values.name, values.businessOrTradingName !== undefined ? values.businessOrTradingName : '', values.branchOrLocationName !== undefined ? values.branchOrLocationName : '');
                await accountContext?.setDefaultOrganisationId(user.defaultOrganisationId, user.organisation?.crmGuid);
            }

            setBranchModalNotification({
                message: 'Your branch/location details have been successfully saved.',
                severity: NotificationSeverity.Success,
            });
            await accountContext?.setShowBranchSelector(true);
        } catch (error) {
            const problemDetails = error as ValidationProblemDetails;
            if (problemDetails) {
                switch (problemDetails.status) {
                    case HttpStatusCode.PreconditionFailed:
                        setBranchModalNotification({
                            message: 'This branch/location name already exists. Please enter a unique branch/location name.',
                            severity: NotificationSeverity.Error,
                        });
                        accountContext?.setShowBranchSelector(true);
                        break;
                    default:
                        setBranchModalNotification({
                            message: 'There was an error saving your branch/location details.',
                            severity: NotificationSeverity.Error,
                        });
                        accountContext?.setShowBranchSelector(true);
                }
            }
        }
    } else {
        throw Error('There was an error saving your branch/location details.');
    }
};

const cancelSave = () => {
    // setDashboardNotification({
    //     message: 'Your organisation and contact details have not been updated.',
    //     severity: NotificationSeverity.Information,
    // });
};

const discardChanges: DiscardProps = {
    cancelButtonTitle: 'Cancel',
    discardButtonTitle: 'Cancel',
    showCancelButton: true,
    locationOnDiscard: '/',
    // onDiscard: cancelSave,
};

const getRedirectionLocationOnError = (errorCode: number, errorType: ErrorType) => {
    if (errorCode === 412) {
        return '/';
    }

    return undefined;
};

const addBranchProps = (
    accounts: AccountInfo[],
    instance: IPublicClientApplication,
    statuses: FormStepStatusDto[],
    accountContext: AccountContextState | null,
)
: WizardStepProps<GetAccountValuesDto> => ({
    initialValues: {
        id: '',
        abn: '',
        branchOrLocationName: '',
        isDefaultOrganisation: undefined,
        businessWebsiteAddress: '',
        businessEmailAddress: '',
        contact: {},
        streetAddress: {},
        postalAddressSameAsStreetAddress: undefined,
        postalAddress: {},
        timestamp: '',
    },
    stepStatuses: statuses,
    loadStepValues: loadAccountDetails(accounts, instance),
    location: '/',
    title: 'Organisation',
    hidingFields: {
        isDefaultOrganisation: (x) => false,
        postalAddress: (x) => x.postalAddressSameAsStreetAddress === true,
        contact: {
            titleOther: (x) => x.contact?.title !== 'Other',
        },
    },
    validateHard: branchSubmitValidation,
    onSaveAndNext: completeAccountDetails(accounts, instance, accountContext),
    bannerTitle: 'Add branch or location',
    discard: discardChanges,
    getRedirectionLocationOnError,
});

export default addBranchProps;
